<template>
  <div
    v-if="tabs.length > 0"
    ref="tab-wrapper"
    class="tab-wrapper"
  >
    <ul
      class="tab-buttons-wrapper"
      role="tablist"
      :aria-label="ariaLabel"
    >
      <li
        v-for="(tab, index) in tabs"
        :id="tabButtonId(index)"
        :key="tab"
        :class="isActive(tab) ? 'v-tab-head-active' : ''"
        class="tab-buttons"
        :aria-controls="tabPanelId(index)"
        :aria-selected="isActive(tab)"
        role="tab"
        @click="tabClick(index, activeTab, tab)"
        @keydown="handleKeydown(index, $event)"
      >
        <button
          ref="tabButton"
          :tabindex="isActive(tab) ? 0 : -1"
          class="tab-button secondary expanded"
        >
          <slot :name="tabHeadSlotName(tab)">
            {{ tab }}
          </slot>
        </button>
      </li>
    </ul>

    <section
      :id="tabPanelId(tabIndex)"
      ref="tabPanel"
      class="tab-panel"
      tabindex="0"
      role="tabpanel"
      :aria-labelledby="tabButtonId(tabIndex)"
    >
      <slot :name="tabPanelSlotName" />
    </section>
  </div>
</template>

<script>
import config from 'config';
import generateRandomId from '../../utils/randomNumber';

export default {
  name: 'Tabs',
  props: {
    initialTab: {
      type: String,
      default: '',
    },
    tabs: {
      type: Array,
      required: true,
      default() {
        return [];
      },
      validator: (value) => (!!(value && value.length > 0)),
    },
    ariaLabel: {
      type: String,
      default: 'Tabs',
    },
  },
  emits: ['tab-click'],
  data() {
    return {
      brand: config.brand,
      activeTab: this.initialTab ? this.initialTab : this.tabs[0],
      tabIndex: this.initialTab
        ? this.tabs.findIndex((_tab) => _tab === this.initialTab)
        : 0,
      width: 0,
      tabWidth: 0,
      id: generateRandomId(),
    };
  },
  computed: {
    tabPanelSlotName() {
      return `v-tab-panel-${this.activeTab}`;
    },
  },
  mounted() {
    this.resizeScroller();
    this.eventHandler();
  },
  beforeUnmount() {
    if (this.tabs.length > 0) { this.ro.unobserve(this.$refs['tab-wrapper']); }
  },
  methods: {
    tabButtonId(index) {
      return `tabs-${this.id}-tab-${index}`;
    },
    tabPanelId(index) {
      return `tabs-${this.id}-panel-${index}`;
    },
    eventHandler() {
      this.ro = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          if (
            entry.contentBoxSize
            && this.width !== entry.contentBoxSize[0].inlineSize
          ) {
            this.resizeScroller();
          }
        });
      });
      if (this.tabs.length > 0) { this.ro.observe(this.$refs['tab-wrapper']); }
    },
    resizeScroller() {
      if (this.$el && this.$el.getBoundingClientRect) {
        this.width = this.$el.getBoundingClientRect().width;
        this.tabWidth = Math.round(this.width / this.tabs.length);
      }
    },
    isActive(tab) {
      return this.activeTab === tab;
    },
    tabHeadSlotName(tabName) {
      return `v-tab-head-${tabName}`;
    },
    handleKeydown(index, event) {
      const { key } = event;
      let newIndex;
      switch (key) {
      case 'ArrowLeft':
        newIndex = index === 0 ? this.tabs.length - 1 : index - 1;
        this.tabClick(newIndex, this.activeTab, this.tabs[newIndex]);
        break;
      case 'ArrowRight':
        newIndex = index === this.tabs.length - 1 ? 0 : index + 1;
        this.tabClick(newIndex, this.activeTab, this.tabs[newIndex]);
        break;
      case 'Home':
        event.preventDefault();
        this.tabClick(0, this.activeTab, this.tabs[0]);
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        this.tabClick(newIndex, this.activeTab, this.tabs[newIndex]);
        break;
      case 'Tab':
        if (!event.shiftKey) {
          event.preventDefault();
          this.$nextTick(() => {
            this.$refs.tabPanel.focus();
          });
        }
        break;
      default:
        break;
      }
    },
    tabClick(index, activeTab, tabName) {
      this.activeTab = tabName;
      this.tabIndex = index;
      this.$emit('tab-click', index, activeTab, tabName);
      this.$nextTick(() => {
        this.$refs.tabButton[index].focus();
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.tab-buttons-wrapper {
  margin-left: rem-calc(0);
  margin-bottom: rem-calc(0);
  align-items: center;
  overflow-x: auto;
  display: flex;
  flex-flow:row;
  width:100%;
  border-bottom: solid 1px $gray-3-color;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.tab-buttons-wrapper::-webkit-scrollbar {
  display: none;
}
.tab-buttons {
  list-style-type: none;
  flex: 1;
}

.tab-button {
  width: 100%;
  border: none;
  padding: rem-calc(12 4);
  white-space: nowrap;
  border-radius: 0%;
  transition: background-color 150ms ease-out;

  &:hover {
    cursor: pointer;
    background-color: $hover-color;
  }

  @media (prefers-reduced-motion: reduce) {
      transition: none;
  }

  &:focus,
  &:hover {
    border: none;
  }
  &.secondary {
    color: $gray-1-color;
    line-height: rem-calc(20);
    flex-grow: 1;
  }
}
.v-tab-head-active .tab-button.secondary {
  color: $black;
  font-weight: $font-weight-heavy;
}
.v-tab-head-active{
  border-bottom: solid 3px $black;
}
</style>
